import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import AsyncSelect from 'react-select/async';
import debounce from 'lodash.debounce';

import Copy from '@components/atoms/copy/copy';

import loadGooglePlacesSDK from '../../helpers/loadGooglePlacesSDK';

import StyledWishLocation from './wishLocation.style';

const WishLocation = ({
  googleMapsKey,
  countryRestriction = ['de', 'at', 'ch'],
  inputProps,
  onSDKLoaded = false,
  labels,
  required = false
}) => {
  const [hasConsent, setConsent] = useState(false);
  const [googleMapsPlacesLoaded, setGoogleMapsPlacesLoaded] = useState(null);

  const giveConsent = () => {
    // Cookie first stuff
    setConsent(true); // for storybook
  };

  const onSuggestionsFetchRequested = (inputValue, callback) => {
    if (hasConsent && googleMapsPlacesLoaded) {
      if (!inputValue) {
        callback([]);
        return;
      }

      // uses the Place Autocomplete Service of the Google Places API
      // https://developers.google.com/maps/documentation/javascript/place-autocomplete?hl=en#place_autocomplete_service
      // Also see documentation: https://developers.google.com/maps/documentation/javascript/reference/places-autocomplete-service
      // Returns up to 5 predictions
      new window.google.maps.places.AutocompleteService().getPlacePredictions(
        {
          input: inputValue,
          types: ['(cities)'],
          language: 'de',
          componentRestrictions: { country: countryRestriction }
        },
        (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK) {
            const options = predictions.map(prediction => ({
              placeId: prediction.place_id,
              label: prediction.description,
              value: 'userResidence'
            }));
            callback(options);
          }
        }
      );
    }
  };

  const checkGooglePlacesSDK = consent => {
    // load google maps api (with library places)
    if (consent && !googleMapsPlacesLoaded && googleMapsKey) {
      loadGooglePlacesSDK(googleMapsKey).then(() => {
        if (onSDKLoaded) onSDKLoaded(true);
        setGoogleMapsPlacesLoaded(true);
      });
    }
  };

  useEffect(() => {
    // condidionally load google maps SDK
    checkGooglePlacesSDK(hasConsent);
  }, [hasConsent]);

  useEffect(() => {
    // get current consent for google maps in user centrics
    const consent = true; // cookie first check
    setConsent(consent);
  }, []);

  const debouncedLoadOptions = debounce(onSuggestionsFetchRequested, 300);

  return (
    <StyledWishLocation>
      {!hasConsent ? (
        <div className="wishLocation__no-consent">
          <div className="wishLocation__container">
            <Copy className="wishLocation__consent-copy">{labels.giveConsentCopy}</Copy>
            <button
              type="button"
              className="wishLocation__consent-details"
              onClick={() => setConsent(true)}
            >
              <Copy tag="span">{labels.giveConsentDetailsCta}</Copy>
            </button>
            <button
              type="button"
              className="autosuggest__consent-btn"
              onClick={() => giveConsent()}
            >
              {labels.giveConsentCta}
            </button>
          </div>
        </div>
      ) : (
        <AsyncSelect
          {...inputProps}
          cacheOptions
          isClearable
          defaultOptions={[]}
          noOptionsMessage={({ inputValue }) =>
            !inputValue ? 'Search for your city' : 'No options'
          }
          className="react-select-container"
          classNamePrefix="react-select"
          loadOptions={debouncedLoadOptions}
          required={required}
        />
      )}
    </StyledWishLocation>
  );
};

WishLocation.propTypes = {
  inputProps: PropTypes.shape().isRequired,
  countryRestriction: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSDKLoaded: PropTypes.func,
  googleMapsKey: PropTypes.string.isRequired,
  labels: PropTypes.shape({
    giveConsentCopy: PropTypes.string,
    giveConsentDetailsCta: PropTypes.string,
    giveConsentCta: PropTypes.string
  }).isRequired,
  required: PropTypes.bool
};

export default WishLocation;
